import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  CardActionArea,
  CardContent,
  CardHeader,
  CardProps,
  Card as MuiCard,
  Box,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

import { createSx } from '../../utils';
import Link, { LinkProps } from '../Link';
import ActionMenu from '../ActionMenu';
import EllipsedTypography from '../Tooltip/EllipsedTypography';

const sx = createSx({
  actionArea: {
    height: '100%'
  },
  description: {
    color: 'neutral.onSurfaceVariant'
  }
});
interface Props extends CardProps {
  children?: React.ReactNode;
  title?: string;
  description?: string | null;
  testID: string;
  redirectLink?: Omit<LinkProps, 'memorizeCurrentLocation'>;
  moreActionItems?: React.ReactElement[];
  Icon?: SvgIconComponent;
  isCondensed?: boolean;
  severity?: 'success' | 'error';
}

const Card = ({
  children,
  testID,
  title,
  description,
  redirectLink,
  moreActionItems,
  Icon,
  isCondensed,
  severity
}: Props) => {
  const { t } = useTranslation();

  const cardTitle = title ? (
    <EllipsedTypography
      testID='card-title'
      variant='subtitle1Bold'
      sx={{
        color: redirectLink ? 'primary.main' : 'neutral.onSurface'
      }}
    >
      {title}
    </EllipsedTypography>
  ) : null;

  const getMoreActions = () => (
    <ActionMenu tooltipText={t('moreActions')} horizontalIcon>
      {({ onClose }) => {
        return moreActionItems?.map((item: React.ReactElement) => {
          return React.cloneElement(item, {
            onClick: onClose,
            isMenuItem: true
          });
        });
      }}
    </ActionMenu>
  );

  const cardContent = (
    <CardContent className={cn({ isCondensed })}>
      <CardHeader
        disableTypography
        avatar={Icon && <Icon color={redirectLink ? 'primary' : 'inherit'} />}
        title={cardTitle}
        subheader={description ? (
          <EllipsedTypography
            testID='card-description'
            variant='body2'
            maxLines={2}
            sx={sx.description}
          >
            {description}
          </EllipsedTypography>) : null}
        action={(moreActionItems && moreActionItems.length > 0) ? getMoreActions() : null}
      />
      {children && <Box sx={{
        color: redirectLink ? 'neutral.onSurface' : 'inherit',
        ml: Icon ? 4 : 0
      }}>
        {children}
      </Box>}
    </CardContent>
  );

  return (
    <MuiCard data-test-id={testID} className={cn(redirectLink && 'clickable', severity)}>
      {redirectLink ? (
        <CardActionArea sx={sx.actionArea}>
          <Link
            {...redirectLink}
            testID={`${testID}-link`}
            memorizeCurrentLocation
          >
            {cardContent}
          </Link>
        </CardActionArea>
      ) : cardContent}
    </MuiCard>
  );
};

export default Card;